import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { getWeatherIcon, getWeatherDescription } from '../../constants/weatherCodes';

const DailyForecast = ({ weather }) => {
  return (
    <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur rounded-xl shadow-lg p-5 border border-orange-100 dark:border-gray-700 animate-on-scroll card-hover hover-lift">
      <h3 className="font-semibold text-lg mb-4 dark:text-gray-100">📅 7-Day Forecast</h3>
      <div className="space-y-3">
        {weather?.daily?.time && (() => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          // Find today's index in the daily array
          const todayIndex = weather.daily.time.findIndex(dateStr => {
            const date = new Date(dateStr);
            date.setHours(0, 0, 0, 0);
            return date.getTime() === today.getTime();
          });
          
          const startIndex = todayIndex >= 0 ? todayIndex : 0;
          
          return weather.daily.time.slice(startIndex, startIndex + 7).map((date, idx) => {
            const day = new Date(date);
            const isToday = idx === 0;
          
            return (
              <div 
                key={idx} 
                className={`p-4 rounded-lg ${
                  isToday ? 'bg-gradient-to-r from-orange-50 to-pink-50 dark:from-orange-900/20 dark:to-pink-900/20 border border-orange-200 dark:border-orange-700' :
                  'bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600'
                }`}
              >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="font-semibold dark:text-gray-100">
                    {isToday ? 'Today' : day.toLocaleDateString('en-US', { weekday: 'long' })}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </p>
                </div>
                
                <div className="flex items-center gap-3">
                  <span className="text-3xl float-animation" style={{animationDelay: `${idx * 0.1}s`}}>{getWeatherIcon(weather.daily.weather_code?.[startIndex + idx] ?? 0)}</span>
                  <div className="text-right">
                    <p className="font-bold text-lg dark:text-gray-100">
                      {Math.round(weather.daily.temperature_2m_max?.[startIndex + idx] ?? 0)}°
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {Math.round(weather.daily.temperature_2m_min?.[startIndex + idx] ?? 0)}°
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Additional daily details */}
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="text-center">
                  <p className="text-gray-500 dark:text-gray-400">Precip</p>
                  <div>
                    {weather.daily.precipitation_probability_max?.[startIndex + idx] !== undefined && (
                      <p className="font-medium text-blue-600 dark:text-blue-400">
                        {weather.daily.precipitation_probability_max[startIndex + idx]}%
                      </p>
                    )}
                    {weather.daily.precipitation_sum?.[startIndex + idx] !== undefined && weather.daily.precipitation_sum[startIndex + idx] > 0 && (
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {weather.daily.precipitation_sum[startIndex + idx].toFixed(1)}"
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-gray-500 dark:text-gray-400">Wind</p>
                  <p className="font-medium dark:text-gray-200">
                    {weather.daily.wind_speed_10m_max?.[startIndex + idx] !== undefined 
                      ? `${Math.round(weather.daily.wind_speed_10m_max[startIndex + idx])} mph` 
                      : '--'}
                  </p>
                </div>
              </div>
              
              {/* UV Index on separate row */}
              {weather.daily.uv_index_max?.[startIndex + idx] !== undefined && (
                <div className="mt-2 text-center">
                  <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200">
                    UV Index: {Math.round(weather.daily.uv_index_max[startIndex + idx])}
                  </span>
                </div>
              )}
              
              {weather.daily.weather_code?.[startIndex + idx] !== undefined && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 text-center">
                  {getWeatherDescription(weather.daily.weather_code[startIndex + idx])}
                </p>
              )}
              </div>
            );
          });
        })()}
      </div>
    </div>
  );
};

DailyForecast.propTypes = {
  weather: PropTypes.shape({
    daily: PropTypes.shape({
      time: PropTypes.arrayOf(PropTypes.string),
      temperature_2m_max: PropTypes.arrayOf(PropTypes.number),
      temperature_2m_min: PropTypes.arrayOf(PropTypes.number),
      weather_code: PropTypes.arrayOf(PropTypes.number),
      precipitation_probability_max: PropTypes.arrayOf(PropTypes.number),
      precipitation_sum: PropTypes.arrayOf(PropTypes.number),
      wind_speed_10m_max: PropTypes.arrayOf(PropTypes.number),
      uv_index_max: PropTypes.arrayOf(PropTypes.number)
    })
  })
};

export default memo(DailyForecast);